import { useState, type ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  error?: string;
  required?: boolean;
  icon?: ReactNode;
  /** react-hook-form `register(...)` result — spread onto the input. */
  registration?: Record<string, unknown>;
}

/**
 * Accessible form field with label, error state, password show/hide, and RTL
 * support. Designed to consume react-hook-form's `register()` output via the
 * `registration` prop so inputs stay uncontrolled (performant, no re-renders).
 */
export function FormField({
  id,
  label,
  type = 'text',
  placeholder,
  autoComplete,
  error,
  required,
  icon,
  registration,
}: FieldProps) {
  const [show, setShow] = useState(false);
  const isPassword = type === 'password';
  const inputType = isPassword ? (show ? 'text' : 'password') : type;
  const hasError = Boolean(error);

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="text-xs font-medium text-brand-muted">
        {label}
        {required && <span className="text-accent ms-1">*</span>}
      </label>

      <div
        className={`relative flex items-center rounded-lg bg-surface-card border transition-colors ${
          hasError
            ? 'border-brand-danger/40 focus-within:border-brand-danger'
            : 'border-surface-border focus-within:border-accent/40'
        }`}
      >
        {icon && (
          <span className="ps-3 text-brand-muted" aria-hidden>
            {icon}
          </span>
        )}
        <input
          id={id}
          type={inputType}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          aria-invalid={hasError}
          aria-describedby={hasError ? `${id}-error` : undefined}
          className="flex-1 w-full bg-transparent px-3 py-2.5 text-sm text-brand-white placeholder:text-brand-muted/60 focus:outline-none"
          {...(registration ?? {})}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShow((s) => !s)}
            className="pe-3 text-xs text-brand-muted hover:text-brand-white transition-colors"
            tabIndex={-1}
          >
            {show ? 'Hide' : 'Show'}
          </button>
        )}
      </div>

      {hasError && (
        <p id={`${id}-error`} className="flex items-center gap-1 text-xs text-brand-danger" role="alert">
          <AlertCircle size={12} />
          {error}
        </p>
      )}
    </div>
  );
}